<script setup lang="ts">
import type { KUICoreRegionSwitcherOption } from '~kui/components/KUI/Core/RegionSwitcher/KUICoreRegionSwitcher.vue';

import { Heading, Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';

interface FooterLink {
  text: string;
  to: string;
  external?: boolean;
  class?: string;
}

interface Props {
  upperLinks: FooterLink[];
  lowerLinks: FooterLink[];
  copyright: string;
  showAddressUs?: boolean;
  showAddressAu?: boolean;
  logoIllustration?: string;
  /** Geo regions for the footer region switcher. Omit/empty to hide it. */
  regions?: KUICoreRegionSwitcherOption[];
}

withDefaults(defineProps<Props>(), {
  showAddressUs: true,
  showAddressAu: true,
  logoIllustration: 'anyway-logo',
  regions: () => [],
});

const emit = defineEmits<{
  regionSwitch: [region: KUICoreRegionSwitcherOption, event: MouseEvent];
}>();
</script>

<template>
  <!-- Outer container -->
  <div class="p-2 bg-background relative  z-40">
    <div class="bg-primary overflow-hidden rounded-4xl ">
      <div class="flex flex-col gap-8 px-12 py-12">
        <!-- Top row: left content + addresses -->
        <div class="flex flex-col justify-between gap-8 md:flex-row">
          <!-- LEFT COLUMN -->
          <div class="flex flex-col gap-8">
            <!-- Logo -->
            <KUICoreIllustration
              :illustration="logoIllustration"
              class="pointer-events-auto w-36 text-white"
              alt="Anyway logo"
            />
            <!-- GET INVOLVED + Region -->
            <div class="flex flex-col gap-8 sm:flex-row sm:gap-16">
              <!-- GET INVOLVED -->
              <div class="flex flex-col gap-2">
                <Heading
                  weight="normal"
                  class="text-background mb-2 tracking-wider uppercase"
                  size="sm"
                  as="h5"
                >
                  GET INVOLVED
                </Heading>
                <div class="flex flex-col gap-1">
                  <KUICoreLink
                    v-for="(link, index) in upperLinks"
                    :key="`upper-link-${index}`"
                    :to="link.to"
                    :text="link.text"
                    :is-external="link.external || false"
                    size="xl"
                    :class="link.class || 'text-background'"
                  />
                </div>
              </div>
              <!-- Region -->
              <KUICoreRegionSwitcher
                v-if="regions.length"
                :regions="regions"
                :heading="$t('unified.geoRegion.switcher.heading')"
                :nav-label="$t('unified.geoRegion.switcher.ariaLabel')"
                @switch="(region, event) => emit('regionSwitch', region, event)"
              />
            </div>
            <!-- Social icons -->
            <div class="mt-6 flex items-center gap-4">
              <KUICoreButtonSocial
                link="https://www.linkedin.com/company/anywayai"
                aria-label="linkedin"
                :icon-path="ICONS.linkedin01"
              />
              <KUICoreButtonSocial
                link="https://www.tiktok.com/@anywaycareers"
                aria-label="tiktok"
                :icon-path="ICONS.tiktok"
              />
              <KUICoreButtonSocial
                link="https://www.instagram.com/anywaycareers"
                aria-label="instagram"
                :icon-path="ICONS.instagram"
              />
            </div>
          </div>
          <!-- RIGHT COLUMN - addresses only -->
          <div class="flex gap-20">
            <!-- US -->
            <div v-if="showAddressUs" class="flex flex-col gap-1">
              <Heading
                weight="normal"
                class="text-background mb-2 tracking-wider"
                size="sm"
                as="h5"
              >
                North Carolina
              </Heading>
              <Text size="lg" class="text-background leading-relaxed">
                204 N Person <br>
                Street. Raleigh, NC <br>
                27601
              </Text>
            </div>
            <!-- AU -->
            <div v-if="showAddressAu" class="flex flex-col gap-1">
              <Heading
                weight="normal"
                class="text-background mb-2 tracking-wider"
                size="sm"
                as="h5"
              >
                Australia
              </Heading>
              <Text size="lg" class="text-background leading-relaxed">
                Level 8, 418a<br>
                Elizabeth Street,<br>
                Surry Hills,<br>
                Sydney, 2010
              </Text>
            </div>
          </div>
        </div>
        <!-- Legal + Copyright - full width -->
        <div class="text-background flex flex-wrap items-center justify-between gap-6 text-xs">
          <!-- Legal links -->
          <div class="flex flex-col gap-3 sm:flex-row sm:gap-6">
            <KUICoreLink
              v-for="(link, index) in lowerLinks"
              :key="`legal-link-${index}`"
              :to="link.to"
              :text="link.text"
              size="sm"
              class="text-background"
            />
          </div>
          <!-- Copyright -->
          <Text size="sm" class="text-background ml-auto text-right">
            {{ copyright }}
          </Text>
        </div>
      </div>
    </div>
  </div>
</template>
